import React, { useMemo, useState } from 'react';
import { Button, Space, Tag, Typography } from 'antd';
import { AlertOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { AreaLink } from '../AreaLink';
import { MetricLink } from '../MetricLink';
import { resolveBenchmarkRangesForMetric } from '../../helpers/metricRules';
import { resolveBenchmarkBand } from './benchmarkUtils';

const { Text } = Typography;

type KpiField = 'average' | 'min' | 'max' | 'count' | 'sum' | 'median' | 'variance' | 'stddev';

interface CommonSectionProps {
    section: any;
    data: any[];
    areas: any[];
    areaTypes: any[];
    metrics: any[];
    previewAreaId: number;
    onAreaClick?: (areaId: number) => void;
    onMetricClick?: (metricKey: string, metricId: number | undefined, areaId: number) => void;
}

const FIELD_LABELS: Record<string, string> = {
    average: 'Average',
    min: 'Min',
    max: 'Max',
    count: 'Count',
    sum: 'Sum',
    median: 'Median',
    variance: 'Variance',
    stddev: 'Std Dev',
};

const DEFAULT_FIELDS: KpiField[] = ['average', 'min', 'max', 'count', 'sum', 'median', 'variance', 'stddev'];
const LOW_BENCHMARK_MAX_SCORE = 2;

const normalizeField = (field: string) => (field === 'avg' ? 'average' : field);

const getFieldValue = (row: any, field: KpiField) => {
    if (field === 'average') {
        return row.average ?? row.avg;
    }
    return row[field];
};

const getBenchmarkTagColor = (label: string) => {
    if (label === 'Poor') return 'red';
    if (label === 'Fair') return 'volcano';
    return 'default';
};

const formatValue = (value?: number | null, unit?: string | null) => {
    if (value === undefined || value === null || Number.isNaN(value)) return '--';
    const formatted = Number(value).toFixed(2);
    return unit ? `${formatted} ${unit}` : formatted;
};

export const ReportWarningCard: React.FC<CommonSectionProps> = ({
    section,
    data,
    areas,
    metrics,
    previewAreaId,
    onAreaClick,
    onMetricClick,
}) => {
    const [expanded, setExpanded] = useState(false);
    const warnings = useMemo(() => {
        const areaLookup = new Map<number, string>();
        areas.forEach((a: any) => {
            if (a?.id && a?.name) areaLookup.set(a.id, a.name);
        });

        const metricLookup = new Map<string, any>();
        metrics.forEach((m: any) => {
            if (m?.key) metricLookup.set(m.key, m);
        });

        const areaById = new Map<number, any>();
        areas.forEach((a: any) => {
            if (a?.id) areaById.set(a.id, a);
        });

        const configuredFields = Array.isArray(section.fields)
            ? section.fields.map((f: string) => normalizeField(f)).filter(Boolean)
            : [];

        const warningRows: Array<{
            key: string;
            metricKey: string;
            metricName: string;
            metricId?: number;
            areaId?: number;
            areaName?: string;
            field: KpiField;
            value: number;
            unit?: string | null;
            benchmarkLabel: string;
            benchmarkScore: number;
        }> = [];

        const rows = Array.isArray(data) ? data : [];
        rows.forEach((row: any, idx: number) => {
            const metricKey = row.metricKey || section.metrics?.[0] || 'metric';
            const metric = metricLookup.get(metricKey);
            const metricName = metric?.name || metricKey;
            const targetArea = row.areaId ? areaById.get(row.areaId) : areaById.get(previewAreaId);
            const benchmarkRanges = resolveBenchmarkRangesForMetric(metric, targetArea);
            if (!benchmarkRanges) return;

            const availableFields = DEFAULT_FIELDS.filter((field) => {
                if (field === 'average') return row.average !== undefined || row.avg !== undefined;
                return row[field] !== undefined;
            });

            const fieldsToCheck: KpiField[] = configuredFields.length
                ? configuredFields.filter((f: string): f is KpiField => DEFAULT_FIELDS.includes(f as KpiField))
                : availableFields;

            const selectedField = fieldsToCheck.find((field) => {
                const value = getFieldValue(row, field);
                return value !== undefined && value !== null && !Number.isNaN(value);
            });

            if (!selectedField) return;
            const value = getFieldValue(row, selectedField);
            if (value === undefined || value === null || Number.isNaN(value)) return;

            const benchmarkBand = resolveBenchmarkBand(value, benchmarkRanges);
            if (!benchmarkBand) return;
            if (benchmarkBand.score > LOW_BENCHMARK_MAX_SCORE) return;

            warningRows.push({
                key: `${metricKey}-${selectedField}-${row.areaId ?? idx}`,
                metricKey,
                metricName,
                metricId: metric?.id,
                areaId: row.areaId,
                areaName: row.areaId ? areaLookup.get(row.areaId) : undefined,
                field: selectedField,
                value,
                unit: row.unit,
                benchmarkLabel: benchmarkBand.label,
                benchmarkScore: benchmarkBand.score,
            });
        });

        return warningRows;
    }, [areas, data, metrics, previewAreaId, section.fields, section.metrics, section.title]);

    if (warnings.length === 0) {
        return null;
    }

    const primaryWarning = warnings[0];
    const hiddenWarningCount = Math.max(warnings.length - 1, 0);
    const primaryWarningValue = formatValue(primaryWarning.value, primaryWarning.unit);

    const renderMetricLabel = (warning: (typeof warnings)[number], compact = false) => {
        if (onMetricClick) {
            return (
                <MetricLink
                    metricKey={warning.metricKey}
                    metricName={warning.metricName}
                    metricId={warning.metricId}
                    areaId={warning.areaId || previewAreaId}
                    onClick={onMetricClick}
                    style={compact ? { fontSize: 13 } : undefined}
                />
            );
        }

        return compact ? <Text strong style={{ fontSize: 13 }}>{warning.metricName}</Text> : <Text strong>{warning.metricName}</Text>;
    };

    const renderAreaLabel = (warning: (typeof warnings)[number]) => {
        if (!warning.areaName) return null;
        if (onAreaClick && warning.areaId) {
            return (
                <AreaLink
                    areaId={warning.areaId}
                    areaName={warning.areaName}
                    onClick={onAreaClick}
                    style={{ fontSize: 12 }}
                />
            );
        }

        return (
            <Text type="secondary" style={{ fontSize: 12 }}>
                {warning.areaName}
            </Text>
        );
    };

    return (
        <div className={`report-warning-card ${expanded ? 'report-warning-card-expanded' : ''}`}>
            <div className="report-warning-summary-row">
                <div className="report-warning-summary-main">
                    <span className="report-warning-summary-icon">
                        <AlertOutlined />
                    </span>
                    <span className="report-warning-summary-count">{warnings.length} {warnings.length === 1 ? 'warning' : 'warnings'}</span>
                    <div className="report-warning-summary-content">
                        <Space size={6} wrap={false} className="report-warning-summary-inline">
                            {renderMetricLabel(primaryWarning, true)}
                            <Text className="report-warning-summary-text">
                                is {primaryWarning.benchmarkLabel.toLowerCase()} at {primaryWarningValue}
                            </Text>
                            {primaryWarning.areaName ? (
                                <>
                                    <Text type="secondary" className="report-warning-summary-text">
                                        in
                                    </Text>
                                    {renderAreaLabel(primaryWarning)}
                                </>
                            ) : null}
                            {hiddenWarningCount > 0 ? (
                                <Text type="secondary" className="report-warning-summary-text">
                                    +{hiddenWarningCount} more
                                </Text>
                            ) : null}
                        </Space>
                    </div>
                </div>

                <Button
                    type="text"
                    size="small"
                    className="report-warning-toggle"
                    icon={expanded ? <UpOutlined /> : <DownOutlined />}
                    onClick={() => setExpanded((value) => !value)}
                >
                    {expanded ? 'Hide' : 'Show all'}
                </Button>
            </div>

            {expanded ? (
                <div className="report-warning-details">
                    {warnings.map((warning) => (
                        <div key={warning.key} className="report-warning-detail-row">
                            <Space size={8} wrap className="report-warning-detail-main">
                                <Tag color="red">Low KPI</Tag>
                                <Tag color={getBenchmarkTagColor(warning.benchmarkLabel)}>{warning.benchmarkLabel}</Tag>
                                {renderMetricLabel(warning)}
                                <Text>
                                    {FIELD_LABELS[warning.field] || warning.field}: {formatValue(warning.value, warning.unit)}
                                </Text>
                                <Text type="secondary">Band score: {warning.benchmarkScore}/5</Text>
                                {renderAreaLabel(warning)}
                            </Space>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export default ReportWarningCard;
